"use client";

import * as React from "react";
import { ArrowRight, Compass, ShieldCheck, FileSpreadsheet, Package, Layers, Wrench, SlidersHorizontal, CircleCheck as CheckCircle2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface IntroCopy {
  badgeTitle: string;
  badgeStd: string;
  h1: string;
  lead: string;
  requestQuote: string;
  exploreProducts: string;
  shortcutsHeading: string;
  navProducts: string;
  navApplications: string;
  navTools: string;
  navResources: string;
  navQuality: string;
  navAbout: string;
  navContact: string;
  cardTitle: string;
  cardBadge: string;
  hlProductsLabel: string;
  hlProductsCount: string;
  hlGuidesLabel: string;
  hlGuidesCount: string;
  hlQualityLabel: string;
  hlQualityCount: string;
  hlSupportLabel: string;
  hlSupportCount: string;
  alloyBullet: string;
  standardsBullet: string;
  oemBullet: string;
  helpLink: string;
}
const INTRO_COPY: Record<Lang, IntroCopy> = {
  en: {
    badgeTitle: "Global Site Index & Directory",
    badgeStd: "ISO 9044 / ASTM E2016",
    h1: "Sitemap & Page Directory",
    lead: "A complete structural overview of MutualFilter's industrial filtration platform. Quickly locate stainless steel woven wire mesh specifications, sintered multilayer media, wedge wire screens, technical conversion charts, and quality compliance guides.",
    requestQuote: "Request a Quote",
    exploreProducts: "Explore Products",
    shortcutsHeading: "Quick Navigation Shortcuts",
    navProducts: "Products",
    navApplications: "Applications",
    navTools: "Tools",
    navResources: "Technical Resources",
    navQuality: "Quality & Supply",
    navAbout: "About Us",
    navContact: "Contact",
    cardTitle: "Platform Content Directory",
    cardBadge: "MutualFilter B2B",
    hlProductsLabel: "Products & Wire Cloth",
    hlProductsCount: "30+ Specs",
    hlGuidesLabel: "Calculators & Charts",
    hlGuidesCount: "15+ Guides",
    hlQualityLabel: "Quality Compliance",
    hlQualityCount: "ASTM / ISO",
    hlSupportLabel: "B2B Engineering Support",
    hlSupportCount: "Global OEM",
    alloyBullet: "Covers AISI 304, 304L, 316, 316L, 904L and specialty alloys",
    standardsBullet: "Standards: ISO 9044, ASTM E2016 with EN 10204 3.1 MTR traceability",
    oemBullet: "Manufactured in Anping, Hebei: custom coils, discs & cylindrical screens",
    helpLink: "Need help finding a specific specification?"
  },
  zh: {
    badgeTitle: "网站架构与全局索引",
    badgeStd: "ISO 9044 / ASTM E2016",
    h1: "网站地图与全局导航索引",
    lead: "MutualFilter 官方网站完整架构概览。查阅我们全系不锈钢编织丝网、多层烧结过滤网、楔形滤网产品页面、工业应用技术方案、工程孔径计算图表及质量控制体系。",
    requestQuote: "索取技术报价",
    exploreProducts: "浏览产品系列",
    shortcutsHeading: "常用主要页面直达",
    navProducts: "工业产品系列",
    navApplications: "应用与工艺流程",
    navTools: "工具",
    navResources: "技术资料库",
    navQuality: "质量与供应链",
    navAbout: "关于互拓",
    navContact: "联系工程团队",
    cardTitle: "网站内容结构与资源",
    cardBadge: "MutualFilter B2B",
    hlProductsLabel: "编织丝网与烧结网产品",
    hlProductsCount: "30+ 规格",
    hlGuidesLabel: "工程计算与孔径图表",
    hlGuidesCount: "15+ 指南",
    hlQualityLabel: "标准合规与材质质保",
    hlQualityCount: "ASTM / ISO",
    hlSupportLabel: "全球询价与工程支持",
    hlSupportCount: "全球 OEM",
    alloyBullet: "覆盖 AISI 304、304L、316、316L、904L 等工业耐腐蚀合金",
    standardsBullet: "执行标准：ISO 9044、ASTM E2016，可按订单要求提供 EN 10204 3.1 质保证书",
    oemBullet: "安平生产基地，支持 OEM 定制分条、冲压片、滤筒焊接加工",
    helpLink: "需要查找具体规格或寻求工程协助？"
  },
  es: {
    badgeTitle: "Índice global y directorio del sitio",
    badgeStd: "ISO 9044 / ASTM E2016",
    h1: "Mapa del sitio y directorio de páginas",
    lead: "Estructura completa de la plataforma de filtración industrial de MutualFilter. Localice especificaciones de malla metálica tejida de acero inoxidable, medios sinterizados multicapa, tamices de alambre en cuña, tablas técnicas y guías de calidad.",
    requestQuote: "Solicitar cotización",
    exploreProducts: "Explorar productos",
    shortcutsHeading: "Accesos directos de navegación",
    navProducts: "Productos",
    navApplications: "Aplicaciones",
    navTools: "Herramientas",
    navResources: "Recursos técnicos",
    navQuality: "Calidad y suministro",
    navAbout: "Sobre nosotros",
    navContact: "Contacto",
    cardTitle: "Directorio de contenidos de la plataforma",
    cardBadge: "MutualFilter B2B",
    hlProductsLabel: "Productos y telas metálicas",
    hlProductsCount: "30+ espec.",
    hlGuidesLabel: "Calculadoras y tablas",
    hlGuidesCount: "15+ guías",
    hlQualityLabel: "Conformidad de calidad",
    hlQualityCount: "ASTM / ISO",
    hlSupportLabel: "Soporte de ingeniería B2B",
    hlSupportCount: "OEM global",
    alloyBullet: "Cubre AISI 304, 304L, 316, 316L, 904L y aleaciones especiales",
    standardsBullet: "Normas: ISO 9044, ASTM E2016 con trazabilidad EN 10204 3.1 MTR",
    oemBullet: "Fabricación en Anping, Hebei: bobinas cortadas, discos y tamices cilíndricos a medida",
    helpLink: "¿Necesita ayuda para encontrar una especificación concreta?"
  },
  de: {
    badgeTitle: "Globaler Seitenindex & Verzeichnis",
    badgeStd: "ISO 9044 / ASTM E2016",
    h1: "Sitemap & Seitenverzeichnis",
    lead: "Vollständige Strukturübersicht der industriellen Filtrationsplattform von MutualFilter. Finden Sie Spezifikationen für Edelstahl-Drahtgewebe, mehrlagige Sintermedien, Spaltsiebe, technische Umrechnungstabellen und Qualitätsleitfäden.",
    requestQuote: "Angebot anfordern",
    exploreProducts: "Produkte entdecken",
    shortcutsHeading: "Schnellnavigation",
    navProducts: "Produkte",
    navApplications: "Anwendungen",
    navTools: "Werkzeuge",
    navResources: "Technische Ressourcen",
    navQuality: "Qualität & Versorgung",
    navAbout: "Über uns",
    navContact: "Kontakt",
    cardTitle: "Inhaltsverzeichnis der Plattform",
    cardBadge: "MutualFilter B2B",
    hlProductsLabel: "Produkte & Drahtgewebe",
    hlProductsCount: "30+ Spezifikationen",
    hlGuidesLabel: "Rechner & Tabellen",
    hlGuidesCount: "15+ Leitfäden",
    hlQualityLabel: "Qualitätskonformität",
    hlQualityCount: "ASTM / ISO",
    hlSupportLabel: "B2B-Ingenieursupport",
    hlSupportCount: "Global OEM",
    alloyBullet: "Umfasst AISI 304, 304L, 316, 316L, 904L und Sonderlegierungen",
    standardsBullet: "Normen: ISO 9044, ASTM E2016 mit EN 10204 3.1 MTR-Rückverfolgbarkeit",
    oemBullet: "Fertigung in Anping, Hebei: Spaltbänder, Ronden & Zylinderfilter nach Maß",
    helpLink: "Benötigen Sie Hilfe bei der Suche nach einer bestimmten Spezifikation?"
  },
  fr: {
    badgeTitle: "Index global et plan du site",
    badgeStd: "ISO 9044 / ASTM E2016",
    h1: "Plan du site et répertoire des pages",
    lead: "Vue d'ensemble complète de la structure de la plateforme de filtration industrielle MutualFilter. Retrouvez rapidement les toiles métalliques tissées en acier inoxydable, médias frittés multicouches, crépines wedge wire, tableaux de conversion et guides qualité.",
    requestQuote: "Demander un devis",
    exploreProducts: "Découvrir les produits",
    shortcutsHeading: "Raccourcis de navigation",
    navProducts: "Produits",
    navApplications: "Applications",
    navTools: "Outils",
    navResources: "Ressources techniques",
    navQuality: "Qualité & approvisionnement",
    navAbout: "À propos",
    navContact: "Contact",
    cardTitle: "Répertoire de contenu de la plateforme",
    cardBadge: "MutualFilter B2B",
    hlProductsLabel: "Produits & toiles métalliques",
    hlProductsCount: "30+ spéc.",
    hlGuidesLabel: "Calculateurs & tableaux",
    hlGuidesCount: "15+ guides",
    hlQualityLabel: "Conformité qualité",
    hlQualityCount: "ASTM / ISO",
    hlSupportLabel: "Support technique B2B",
    hlSupportCount: "OEM mondial",
    alloyBullet: "Couvre les alliages AISI 304, 304L, 316, 316L, 904L et nuances spéciales",
    standardsBullet: "Normes : ISO 9044, ASTM E2016 avec traçabilité MTR EN 10204 3.1",
    oemBullet: "Fabrication à Anping, Hebei : bandes refendues, disques et cylindres sur mesure",
    helpLink: "Besoin d'aide pour trouver une spécification précise ?"
  },
  pt: {
    badgeTitle: "Índice global e diretório do site",
    badgeStd: "ISO 9044 / ASTM E2016",
    h1: "Mapa do site e diretório de páginas",
    lead: "Visão geral completa da estrutura da plataforma de filtração industrial da MutualFilter. Localize rapidamente especificações de telas metálicas tecidas em aço inoxidável, meios sinterizados multicamadas, telas em cunha, tabelas técnicas e guias de qualidade.",
    requestQuote: "Solicitar cotação",
    exploreProducts: "Explorar produtos",
    shortcutsHeading: "Atalhos de navegação",
    navProducts: "Produtos",
    navApplications: "Aplicações",
    navTools: "Ferramentas",
    navResources: "Recursos técnicos",
    navQuality: "Qualidade e fornecimento",
    navAbout: "Sobre nós",
    navContact: "Contato",
    cardTitle: "Diretório de conteúdos da plataforma",
    cardBadge: "MutualFilter B2B",
    hlProductsLabel: "Produtos e telas metálicas",
    hlProductsCount: "30+ espec.",
    hlGuidesLabel: "Calculadoras e tabelas",
    hlGuidesCount: "15+ guias",
    hlQualityLabel: "Conformidade de qualidade",
    hlQualityCount: "ASTM / ISO",
    hlSupportLabel: "Suporte de engenharia B2B",
    hlSupportCount: "OEM global",
    alloyBullet: "Abrange AISI 304, 304L, 316, 316L, 904L e ligas especiais",
    standardsBullet: "Normas: ISO 9044, ASTM E2016 com rastreabilidade EN 10204 3.1 MTR",
    oemBullet: "Fabricação em Anping, Hebei: bobinas cortadas, discos e telas cilíndricas sob medida",
    helpLink: "Precisa de ajuda para encontrar uma especificação específica?"
  }
};
export default function SitemapIntro() {
  const {
    lang
  } = useLanguage();
  const c = pick(lang, INTRO_COPY);
  const quickLinks = [{
    label: c.navProducts,
    href: "/ProductsPage",
    icon: Package
  }, {
    label: c.navApplications,
    href: "/ApplicationsPage",
    icon: Compass
  }, {
    label: c.navTools,
    href: "/ToolsPage",
    icon: Wrench
  }, {
    label: c.navResources,
    href: "/TechnicalResourcesPage",
    icon: FileSpreadsheet
  }, {
    label: c.navQuality,
    href: "/QualitySupplyPage",
    icon: ShieldCheck
  }, {
    label: c.navAbout,
    href: "/AboutPage",
    icon: Layers
  }, {
    label: c.navContact,
    href: "/ContactPage",
    icon: SlidersHorizontal
  }];
  const siteHighlights = [{
    label: c.hlProductsLabel,
    count: c.hlProductsCount
  }, {
    label: c.hlGuidesLabel,
    count: c.hlGuidesCount
  }, {
    label: c.hlQualityLabel,
    count: c.hlQualityCount
  }, {
    label: c.hlSupportLabel,
    count: c.hlSupportCount
  }];
  return <section id="sitemapintro" data-nav="light" className="w-full border-b border-border bg-background mf-hero">
      <div className="mf-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left Column: Heading, Lead & Quick Nav Chips */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-4">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-muted px-3 py-1 text-[13px] font-mono tracking-tight text-foreground">
                <Compass className="size-3.5 text-primary" />
                {c.badgeTitle}
              </Badge>
              <Badge variant="outline" className="rounded-md border-border bg-muted px-2.5 py-1 text-[13px] font-mono text-foreground">
                <ShieldCheck className="mr-1 size-3 text-primary inline" />
                {c.badgeStd}
              </Badge>
            </div>

            <div className="space-y-3">
              <h1 className="mf-h1 text-foreground">
                {c.h1}
              </h1>
              <p className="mf-lead text-muted-foreground max-w-2xl">
                {c.lead}
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto pt-1">
              <Button asChild size="lg" className="px-6 font-semibold">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.requestQuote)}</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="px-6 font-semibold bg-card border-border">
                <Link to="/ProductsPage" className="inline-flex items-center gap-2">
                  <span>{pick(lang, COMMON.exploreProducts)}</span>
                </Link>
              </Button>
            </div>

            {/* Quick Navigation Chips */}
            <div className="w-full pt-4 border-t border-border">
              <div className="text-[13px] font-mono uppercase tracking-wider text-muted-foreground mb-2.5">
                {c.shortcutsHeading}
              </div>
              <div className="flex flex-wrap gap-2">
                {quickLinks.map((item, idx) => {
                const Icon = item.icon;
                return <Button key={idx} asChild variant="outline" size="sm" className="gap-1.5 text-[13px] bg-card hover:bg-muted border-border text-foreground">
                      <Link to={item.href}>
                        <Icon className="size-3.5 text-primary" />
                        <span>{item.label}</span>
                      </Link>
                    </Button>;
              })}
              </div>
            </div>
          </div>

          {/* Right Column: Spec / Structural Card */}
          <div className="lg:col-span-5 w-full">
            <Card className="rounded-lg border border-border bg-card">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center justify-between pb-2.5 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Compass className="size-4 text-primary" />
                    <span className="text-sm font-semibold text-foreground">
                      {c.cardTitle}
                    </span>
                  </div>
                  <Badge variant="outline" className="font-mono text-[13px] border-border bg-muted text-foreground">
                    {c.cardBadge}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  {siteHighlights.map((hl, idx) => <div key={idx} data-index={idx} className="rounded-lg border border-border/70 bg-muted/40 p-2.5">
                      <span className="font-mono text-[13px] font-bold text-foreground block">
                        {hl.count}
                      </span>
                      <span className="text-[13px] text-muted-foreground mt-0.5 block">
                        {hl.label}
                      </span>
                    </div>)}
                </div>

                <Separator className="my-3" />

                <div className="space-y-2 text-[13px] text-muted-foreground">
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{c.alloyBullet}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{c.standardsBullet}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{c.oemBullet}</span>
                  </div>
                </div>

                <div className="pt-1">
                  <Button asChild variant="ghost" size="sm" className="w-full text-[13px] text-primary hover:text-primary/90 justify-center gap-1">
                    <Link to="/ContactPage">
                      <span>{c.helpLink}</span>
                      <ArrowRight className="size-3.5" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}